import { useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { Loader2, ArrowRight, AlertTriangle, CheckCircle2 } from "lucide-react";
import { format, addMinutes } from "date-fns";
import { sv } from "date-fns/locale";
import { supabase } from "@/integrations/supabase/client";
// PR-11.0: cascade booking moves to any linked league_match
import { cascadeBookingMove } from "@/lib/bookingLeagueSync";
import { resolveMoveBookingValues } from "@/lib/moveBookingHelpers";
import { useOrganization } from "@/hooks/useOrganization";
import { toast } from "sonner";
import { formatPrice, resolveMoney } from "@/utils/formatPrice";
import {
  resolveBookingTargetPrice,
  bookingKindForPricing,
  type PricingRule,
  type SpecialPriceDate,
} from "@/lib/pricing";
import { checkSlotAvailability } from "@/lib/bookingConflict";

interface MoveBookingDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  booking: {
    id: string;
    court_id: string;
    court_name: string;
    start_time: string;
    end_time: string;
    duration_minutes: number;
    price_total: number | null;            // legacy kronor (Sprint D)
    price_total_minor: number | null;      // öre (preferred)
    currency: string;
    user_id: string;
    booker_name: string;
    status: string;
    // Lager 3b.1 (P3-fix): branch på bookingKind för pris-resolver.
    // Open-match → flat standard; private → per-seat via resolver.
    is_open_match?: boolean | null;
    activity_id?: string | null;
  };
  /** Lager 3b.1: max_players krävs för per-seat-uträkning på målbana. */
  courts: Array<{ id: string; name: string; max_players?: number | null }>;
  existingBookings: Array<{ court_id: string; start_time: string; end_time: string; status: string; id: string }>;
  hallHours: { start: number; end: number };
  hallId: string;
  pricingRules: PricingRule[];
  specialDates?: SpecialPriceDate[];
  timeStep: number;
  adminId: string;
  onMoved: () => void;
  /**
   * UPPDRAG-31: optional pre-fill from a drag-drop. When set, the
   * "TILL" form fields open populated with the drop target instead of
   * empty (so the admin sees what they're confirming). Empty string
   * fields fall back to original via resolveMoveBookingValues, exactly
   * like the menu-opened path. UPPDRAG-29's hasChanged logic still
   * works: if the drop target equals the original (admin dropped on
   * the same slot), hasChanged === false and the button stays
   * disabled — no accidental no-op move.
   */
  initialTarget?: { courtId?: string; date?: string; time?: string } | null;
}

export default function MoveBookingDialog({
  open,
  onOpenChange,
  booking,
  courts,
  existingBookings,
  hallHours,
  hallId,
  pricingRules,
  specialDates = [],
  timeStep,
  adminId,
  onMoved,
  initialTarget,
}: MoveBookingDialogProps) {
  const { t } = useTranslation("admin");
  const { organization } = useOrganization();
  // UPPDRAG-29: form fields start EMPTY ("" = "use original"). Originals
  // are surfaced as Select / Input placeholders below. This avoids the
  // permanent-disabled submit button bug where pre-filled values made
  // hasChanged === false until the admin manually changed something.
  // UPPDRAG-31: when opened from a drag-drop, initialTarget pre-fills
  // the fields so the admin sees the proposed move + confirms via the
  // submit button (no direct RPC call from the grid).
  const [newCourtId, setNewCourtId] = useState<string>(initialTarget?.courtId ?? "");
  const [newDate, setNewDate] = useState<string>(initialTarget?.date ?? "");
  const [newTime, setNewTime] = useState<string>(initialTarget?.time ?? "");
  const [reason, setReason] = useState("");
  const [notifyBooker, setNotifyBooker] = useState(true);
  const [notifyPlayers, setNotifyPlayers] = useState(true);
  const [moving, setMoving] = useState(false);
  const [priceAction, setPriceAction] = useState<"update" | "keep" | "manual">("update");
  const [manualPrice, setManualPrice] = useState("");

  const oldStart = new Date(booking.start_time);
  const oldEnd = new Date(booking.end_time);
  const originalDate = format(oldStart, "yyyy-MM-dd");
  const originalTime = format(oldStart, "HH:mm");

  // UPPDRAG-29: resolve "use original" semantics for downstream
  // computations. effective* fall back to originals; hasChanged is true
  // only when at least one field differs from the original.
  const { effectiveCourtId, effectiveDate, effectiveTime, hasChanged } =
    resolveMoveBookingValues(
      { newCourtId, newDate, newTime },
      { originalCourtId: booking.court_id, originalDate, originalTime },
    );

  // Calculate new times using effective date/time
  const [newHour, newMinute] = effectiveTime.split(":").map(Number);
  const newStart = useMemo(() => {
    const d = new Date(effectiveDate + "T00:00:00");
    d.setHours(newHour, newMinute, 0, 0);
    return d;
  }, [effectiveDate, newHour, newMinute]);
  const newEnd = addMinutes(newStart, booking.duration_minutes);

  // Fetch bookings for the target date (needed for cross-day conflict
  // detection). UPPDRAG-29: query keyed on effectiveDate (so cross-day
  // checks still work when admin picks a new date) but only enabled when
  // the admin actually changed the date — otherwise we already have
  // existingBookings for the original date.
  const dateActuallyChanged = newDate !== "" && newDate !== originalDate;
  const { data: targetDateBookings } = useQuery({
    queryKey: ["move-target-bookings", hallId, effectiveDate],
    queryFn: async () => {
      const dayStart = new Date(effectiveDate + "T00:00:00").toISOString();
      const dayEnd = new Date(effectiveDate + "T23:59:59").toISOString();
      const { data, error } = await supabase
        .from("bookings")
        .select("id, court_id, start_time, end_time, status")
        .eq("status", "confirmed")
        .gte("start_time", dayStart)
        .lte("start_time", dayEnd);
      if (error) throw error;
      return (data ?? []) as Array<{ court_id: string; start_time: string; end_time: string; status: string; id: string }>;
    },
    enabled: open && dateActuallyChanged,
  });

  // Use target date bookings if date changed, otherwise existing bookings
  const bookingsToCheck = dateActuallyChanged ? (targetDateBookings ?? []) : existingBookings;

  // UPPDRAG-33: pre-fetch the customer's default MIT-enabled saved card.
  // Drives the disabled-state of the "Debitera mellanskillnad" select item
  // and surfaces the card-last4 in the success toast so the admin knows
  // which card was charged. Q1=A: hard-error if no saved card — admin
  // can't pick "Debitera" at all if the customer never saved one.
  const { data: savedPaymentMethod } = useQuery({
    queryKey: ["move-booking-saved-card", booking.user_id, organization?.id],
    queryFn: async () => {
      if (!organization?.id) return null;
      const { data, error } = await supabase
        .from("player_payment_methods")
        .select("id, card_brand, card_last4")
        .eq("user_id", booking.user_id)
        .eq("organization_id", organization.id)
        .eq("is_default", true)
        .eq("is_mit_enabled", true)
        .maybeSingle();
      if (error) throw error;
      return data;
    },
    enabled: open && !!organization?.id,
    staleTime: 5 * 60_000,
  });
  const hasSavedCard = !!savedPaymentMethod;

  // ─── Lager 3b.1 (P3-fix) — per-seat-prisresolver-inputs ──────────────────
  // Branch på bookingKind (open_match → standard; private → per-seat).
  // Booker-kategori + invited-kategorier hämtas via useQuery och passas
  // till `resolveBookingTargetPrice` (pure helper, kontext-agnostisk per
  // Lesson 42). F5 guard i per-seat-helpern preserverar exakt court-std
  // för standard-only-bokningar (regression-säkert).
  const bookingKind = bookingKindForPricing({
    is_open_match: booking.is_open_match ?? false,
    activity_id: booking.activity_id ?? null,
  });
  const isPrivate = bookingKind === "private";

  // Lager 3b.1: ETT useQuery för båda bokaren + inbjudna (snapshot från
  // booking_players.category_slug, satt vid bokningsskapande). Audit-
  // upptäckt 2026-05-19: booking_players inkluderar bokaren själv på
  // demo (verifierat via SQL för booking 376a351b). Tidigare design
  // (separat getPlayerCategory för bokaren + alla booking_players som
  // invited) dubbel-räknade bokaren → felaktig per-seat-totaler.
  //
  // Snapshot-source-of-truth: matchar exakt vad som ursprungligen
  // debiterades (price_total_minor satt baserat på samma slugs). Live-
  // kategori via getPlayerCategory används EJ här eftersom delta-bas
  // ska vara consistent med originalpriset.
  const { data: bookingPlayers = [] } = useQuery({
    queryKey: ["move-booking-players", booking.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("booking_players")
        .select("user_id, category_slug")
        .eq("booking_id", booking.id);
      if (error) throw error;
      return (data ?? []).map(p => ({
        user_id: (p as { user_id: string }).user_id,
        // category_slug kan vara NULL för pre-migration-20260314162003-rader
        // → fallback till 'standard' (F5 guard hanterar exakt-standard-fall).
        category_slug: (p as { category_slug: string | null }).category_slug ?? "standard",
      }));
    },
    enabled: open && isPrivate,
    staleTime: 5 * 60_000,
  });

  // Bokarens slug: leta i booking_players. Om bokaren saknas där (legacy
  // bokningar utan booking_players-rad för bokaren) → fallback 'standard'.
  const bookerCategorySlug =
    bookingPlayers.find(p => p.user_id === booking.user_id)?.category_slug ?? "standard";

  // Invited = alla utom bokaren (per-seat-helpern tar dem separat).
  const invitedCategorySlugs = useMemo(
    () => bookingPlayers.filter(p => p.user_id !== booking.user_id).map(p => p.category_slug),
    [bookingPlayers, booking.user_id],
  );

  const newCourtName = courts.find(c => c.id === effectiveCourtId)?.name ?? "Bana";
  const effectiveCourtSeatCount =
    courts.find(c => c.id === effectiveCourtId)?.max_players ?? 4;

  // Check overlap against target date bookings (using effective court id
  // so the check is correct even when the admin only changed time/date).
  const hasOverlap = useMemo(() => {
    return !checkSlotAvailability(effectiveCourtId, newStart, newEnd, bookingsToCheck, booking.id).available;
  }, [bookingsToCheck, effectiveCourtId, newStart, newEnd, booking.id]);

  // Check within operating hours
  const withinHours = newHour >= hallHours.start && (newEnd.getHours() <= hallHours.end || (hallHours.end === 24 && newEnd.getHours() === 0));

  // ─── Lager 3b.1 (P3-fix) + Path B+ — canonical pris-resolver ────────────
  // EN källa för display, finalPrice, RPC-payload och charge-belopp.
  // resolveBookingTargetPrice branch:ar internt: open_match → standard;
  // private → per-seat via calculatePrivateBookingPerSeatPrice (med F5
  // all-standard guard som preserverar exakt court-std-pris för
  // bokningar utan medlemmar — regression-säkert). Kontext-agnostisk
  // helper (Lesson 42) — samma resolver används av create-flow och
  // reschedule-flow.
  const targetPrice = useMemo(() => resolveBookingTargetPrice({
    bookingKind,
    targetSlot: {
      hallId,
      startTime: newStart,
      durationMinutes: booking.duration_minutes,
      seatCount: effectiveCourtSeatCount,
      pricingRules,
      specialDates,
    },
    bookerCategorySlug,
    invitedCategorySlugs,
  }), [bookingKind, hallId, newStart, booking.duration_minutes, effectiveCourtSeatCount, pricingRules, specialDates, bookerCategorySlug, invitedCategorySlugs]);

  // Original total i öre (öre-exakt; fallback till price_total*100 för
  // legacy-rader). Delta beräknas öre-baserat — ingen kr→öre→kr-flip.
  const originalTotalMinor = booking.price_total_minor
    ?? Math.round((booking.price_total ?? 0) * 100);
  const deltaMinor = targetPrice.totalMinor - originalTotalMinor;
  // Kr-derived för i18n-strängar (existerande "Mellanskillnad {{amount}} kr").
  // Använd öre→kr / 100 så avrundning är konsekvent.
  const priceDiff = deltaMinor / 100;
  // hasChanged comes from resolveMoveBookingValues above (UPPDRAG-29).

  // Determine final price (öre-baserat — canonical). Charge/refund/RPC
  // läser finalPriceMinor; legacy booking_moves-audit-log behåller kr
  // via /100 för bakåt-kompat.
  const finalPriceMinor = useMemo(() => {
    if (deltaMinor === 0 || priceAction === "update") return targetPrice.totalMinor;
    if (priceAction === "keep") return originalTotalMinor;
    if (priceAction === "manual" && manualPrice) return Math.round(Number(manualPrice) * 100);
    return targetPrice.totalMinor;
  }, [deltaMinor, priceAction, targetPrice.totalMinor, originalTotalMinor, manualPrice]);
  const finalPrice = finalPriceMinor / 100;

  // Available time slots
  const timeSlots = useMemo(() => {
    const slots: string[] = [];
    for (let h = hallHours.start; h < (hallHours.end === 24 ? 24 : hallHours.end); h++) {
      for (let m = 0; m < 60; m += timeStep) {
        slots.push(`${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`);
      }
    }
    return slots;
  }, [hallHours, timeStep]);

  const handleMove = async () => {
    if (!hasChanged || hasOverlap || !withinHours) return;
    setMoving(true);
    try {
      // UPPDRAG-26 (Bug 1): RPC takes price params in MINOR units (öre),
      // not kronor — same fix as the AdminScheduleGrid drag-drop call.
      // Calling with the legacy kronor names caused PostgREST to 404 the
      // function (no signature match), surfacing as "Kunde inte flytta"
      // with no diagnostic for the admin.
      // UPPDRAG-29: use effectiveCourtId (falls back to original when
      // admin only changed the time/date).
      // Lager 3b.1: öre-exakt från resolvern (ingen kr→öre-flip)
      const { data: _data, error } = await supabase.rpc("move_booking", {
        _booking_id: booking.id,
        _new_court_id: effectiveCourtId,
        _new_start_time: newStart.toISOString(),
        _new_end_time: newEnd.toISOString(),
        _new_price_total_minor: finalPriceMinor,
        _new_price_per_hour_minor: targetPrice.perHourMinor,
        // UPPDRAG-31: types regen aligned `_reason` to `string | undefined`
        // (the RPC SQL DEFAULT NULL maps to "absent" in PostgREST). Don't
        // send literal null — TS rejects it now.
        _reason: reason || undefined,
        _admin_id: adminId,
      });

      if (error) throw error;

      // UPPDRAG-33: handle Stripe price difference end-to-end.
      // priceDiff > 0 → invoke `charge-booking-difference` (Q1=A: requires
      //   saved MIT card; we already disabled the select item if !hasSavedCard,
      //   so by the time we get here the card exists OR admin chose another
      //   action like "Bjud på mellanskillnaden" / "Manuellt pris").
      //   On charge failure (Q3=B from Rickard's plan): the move is already
      //   done by the RPC above, the wrapper edge fn left a `failed`
      //   booking_payments row + admin notification, we surface a warning
      //   toast and continue.
      // priceDiff < 0 → existing refund flow (UNCHANGED).
      // Lager 3b.1: charge/refund-belopp från öre-exakt deltaMinor.
      if (priceAction === "update" && deltaMinor !== 0) {
        if (deltaMinor > 0) {
          const amountMinor = deltaMinor;
          const { data: chargeRes, error: chargeErr } = await supabase.functions.invoke(
            "charge-booking-difference",
            {
              body: {
                booking_id: booking.id,
                amount_minor: amountMinor,
                reason: reason || undefined,
              },
            },
          );
          if (chargeErr || !chargeRes?.success) {
            const code = chargeRes?.code ?? "UNKNOWN";
            const errMsg = chargeRes?.error ?? chargeErr?.message ?? "";
            // Q3=B: leave the move done, don't roll back. Admin sees a
            // warning + handles the unpaid difference manually.
            if (code === "NO_SAVED_CARD") {
              toast.warning(
                t("moveBooking.chargeDiffNoCard", {
                  defaultValue:
                    "Bokningen flyttades men kunden har inget sparat kort — mellanskillnaden är obetald.",
                }),
              );
            } else {
              toast.warning(
                t("moveBooking.chargeDiffFailed", {
                  defaultValue:
                    "Bokningen flyttades men mellanskillnaden kunde inte debiteras: {{err}}. Admin-notis skapad.",
                  err: errMsg,
                }),
              );
            }
            console.error("[move-booking] charge-booking-difference failed:", { code, errMsg });
          } else {
            // Success — the wrapper already wrote the booking_payments row
            // as 'paid' + sent customer email/notification. Just acknowledge
            // in the toast so the admin sees what happened.
            toast.success(
              t("moveBooking.chargeDiffSuccess", {
                defaultValue: "Mellanskillnad {{amount}} kr debiterad{{card}}.",
                amount: priceDiff,
                card: savedPaymentMethod?.card_last4
                  ? ` (kort ****${savedPaymentMethod.card_last4})`
                  : "",
              }),
            );
          }
        } else if (deltaMinor < 0) {
          // Check if there's a Stripe payment to refund
          const { data: stripePayments } = await supabase
            .from("booking_payments")
            .select("id, stripe_payment_intent_id")
            .eq("booking_id", booking.id)
            .not("stripe_payment_intent_id", "is", null)
            .limit(1);
          if (stripePayments && stripePayments.length > 0) {
            // UPPDRAG-63 (Bug #2 fix): pass `partial: true` + amount in
            // ÖRE (Stripe-native minor units). Pre-fix, this site sent
            // `amount: Math.abs(priceDiff)` (kronor) but the edge fn
            // ignored the field entirely and refunded the full payment.
            // The refund-payment v17 contract REQUIRES partial=true
            // when amount is set, and the amount must be in minor units.
            // Lager 3b.1: deltaMinor är redan öre-exakt från resolvern.
            const partialAmountMinor = Math.abs(deltaMinor);
            if (partialAmountMinor <= 0) {
              // Defensive — shouldn't reach here because the surrounding
              // branch already gates on `deltaMinor < 0`, but a 0 amount
              // would 400 the edge fn.
              console.warn(
                "[move-booking] computed partial refund amount <= 0 — skipping",
                { deltaMinor, partialAmountMinor },
              );
            } else {
              await supabase.functions.invoke("refund-payment", {
                body: {
                  payment_id: stripePayments[0].id,
                  partial: true,
                  amount: partialAmountMinor,
                  reason: t("moveBooking.priceChangeOnMove"),
                },
              }).catch((err) => {
                console.error("Partial refund failed:", err);
                toast.warning(t("moveBooking.moveSuccessRefundFailed"));
              });
            }
          }
        }
      }

      // Log to booking_moves audit trail — Sprint D dual-write.
      // Lager 3b.1: price_*_minor är öre-exakt från resolvern + finalPriceMinor.
      // price_* (kr legacy) härleds via /100 för bakåt-kompat med läsare
      // som ännu inte migrerat till öre-kolumnerna.
      const manualNum = priceAction === "manual" ? Number(manualPrice) || null : null;
      supabase.from("booking_moves" as any).insert({
        booking_id: booking.id,
        moved_by: adminId,
        from_court_id: booking.court_id,
        from_start_time: booking.start_time,
        from_end_time: booking.end_time,
        to_court_id: effectiveCourtId,
        to_start_time: newStart.toISOString(),
        to_end_time: newEnd.toISOString(),
        reason: reason || null,
        price_old: originalTotalMinor / 100,                  // legacy kronor (härlett)
        price_old_minor: originalTotalMinor,                  // öre (canonical)
        price_new: finalPrice,                                 // legacy kronor (härlett)
        price_new_minor: finalPriceMinor,                     // öre (canonical)
        currency: "SEK",
        price_action: deltaMinor === 0 ? "same" : priceAction,
        // NB: `price_model: targetPrice.model` (per_seat | standard) skulle
        // vara nice-to-have för retroaktiv analys men kräver schema-migration
        // (kolumnen finns ej i booking_moves idag — verifierat 2026-05-19
        // via execute_sql). Lämnas till framtida lager.
        price_manual_amount: manualNum,
        price_manual_amount_minor: manualNum != null ? Math.round(manualNum * 100) : null,
      }).then(() => {}).catch(() => {});

      // Send notifications if checked
      // UPPDRAG-29: notification body uses effectiveTime (falls back to
      // original) so the message is correct even when admin only changed
      // the court.
      if (notifyBooker || notifyPlayers) {
        await supabase.from("notifications").insert({
          user_id: booking.user_id,
          organization_id: organization!.id,  // PR-A: org-scope per Phase 1 RLS
          type: "booking_moved",
          title: t("moveBooking.notificationTitle"),
          body: t("moveBooking.notificationBody", { fromCourt: booking.court_name, fromTime: format(oldStart, "HH:mm"), toCourt: newCourtName, toTime: effectiveTime }) + (reason ? `. ${t("moveBooking.reason")}: ${reason}` : ""),
          data: { booking_id: booking.id },
        });
      }

      // PR-11.0: cascade to linked league_match (no-op if booking is not
      // a league booking). Fire-and-forget — failure is logged but doesn't
      // block the user-visible move success.
      cascadeBookingMove({
        bookingId: booking.id,
        newStartTime: newStart,
        newCourtId: effectiveCourtId,
        reason: reason || null,
        adminUserId: adminId,
      }).catch((e) => console.error("league cascade failed", e));

      toast.success(t("moveBooking.movedSuccess", { court: newCourtName, time: effectiveTime }));
      onMoved();
      onOpenChange(false);
    } catch (err: unknown) {
      // UPPDRAG-31-followup: surface PostgrestError messages too. Supabase
      // RPC errors come back as PostgrestError objects (NOT Error
      // instances) so the previous `err instanceof Error ? err.message`
      // check dropped the diagnostic — admins only saw the generic
      // "Kunde inte flytta bokningen" with no detail. Same fix-shape
      // PR #70 applied to AdminScheduleGrid (which got refactored away
      // by UPPDRAG-31; keeping the dialog version belt-and-suspenders
      // defensive). Also log the full error for browser-console
      // debugging when reproductions surface in prod.
      const message =
        err && typeof err === "object" && "message" in err && typeof (err as { message: unknown }).message === "string"
          ? (err as { message: string }).message
          : err instanceof Error
            ? err.message
            : "";
      toast.error(t("moveBooking.couldNotMove") + (message ? " — " + message : ""));
      console.error("Move booking error:", err);
    } finally {
      setMoving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{t("moveBooking.title")}</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* From */}
          <div className="rounded-lg bg-muted p-3 space-y-1 text-sm">
            <p className="text-xs text-muted-foreground font-medium">{t("moveBooking.from")}</p>
            <p className="font-semibold">{booking.court_name}</p>
            <p>{format(oldStart, "EEEE d MMMM", { locale: sv })} {format(oldStart, "HH:mm")}–{format(oldEnd, "HH:mm")}</p>
            <p className="text-muted-foreground">{t("moveBooking.booker")}: {booking.booker_name}</p>
          </div>

          {/* To */}
          <div className="space-y-3">
            <p className="text-xs text-muted-foreground font-medium">{t("moveBooking.to")}</p>
            <div>
              <Label className="text-xs">{t("moveBooking.date")}</Label>
              <Input type="date" value={newDate} onChange={(e) => setNewDate(e.target.value)} className="h-9 text-sm" />
              {newDate === "" && (
                <p className="text-xs text-muted-foreground mt-1">
                  {t("moveBooking.unchangedHint", { defaultValue: "Oförändrat: {{value}}", value: originalDate })}
                </p>
              )}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">{t("moveBooking.court")}</Label>
                <Select value={newCourtId} onValueChange={setNewCourtId}>
                  <SelectTrigger>
                    <SelectValue placeholder={t("moveBooking.unchangedPlaceholder", { defaultValue: "{{value}} (oförändrad)", value: booking.court_name })} />
                  </SelectTrigger>
                  <SelectContent>
                    {courts.map(c => (
                      <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">{t("moveBooking.time")}</Label>
                <Select value={newTime} onValueChange={setNewTime}>
                  <SelectTrigger>
                    <SelectValue placeholder={t("moveBooking.unchangedPlaceholder", { defaultValue: "{{value}} (oförändrad)", value: originalTime })} />
                  </SelectTrigger>
                  <SelectContent>
                    {timeSlots.map(t => {
                      const tStart = new Date(effectiveDate + "T00:00:00");
                      tStart.setHours(parseInt(t.split(":")[0]), parseInt(t.split(":")[1]));
                      const tEnd = addMinutes(tStart, booking.duration_minutes);
                      const taken = bookingsToCheck.some(b =>
                        b.court_id === effectiveCourtId && b.id !== booking.id && b.status !== "cancelled" &&
                        new Date(b.start_time) < tEnd && new Date(b.end_time) > tStart
                      );
                      return (
                        <SelectItem key={t} value={t} disabled={taken}>
                          {t} {taken ? "⛔" : ""}
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          {/* Validation errors */}
          {hasOverlap && (
            <div className="flex items-center gap-2 text-sm text-destructive">
              <AlertTriangle className="h-4 w-4" />
              {t("moveBooking.courtOccupied", { court: newCourtName, from: effectiveTime, to: format(newEnd, "HH:mm") })}
            </div>
          )}
          {!withinHours && (
            <div className="flex items-center gap-2 text-sm text-destructive">
              <AlertTriangle className="h-4 w-4" />
              {t("moveBooking.outsideHours")}
            </div>
          )}
          {hasChanged && !hasOverlap && withinHours && (
            <div className="flex items-center gap-2 text-sm text-green-600">
              <CheckCircle2 className="h-4 w-4" />
              {t("moveBooking.slotAvailable")}
            </div>
          )}

          {/* Price change + action selector
              Lager 3b.1: hasChanged-gate kvar (operatör-låst beslut Test 2 —
              ingen "0 kr"-brus på orörd dialog). Belopp visas öre-exakt via
              valueMinor från resolvern; absolut delta visas också öre-exakt. */}
          {hasChanged && deltaMinor !== 0 && (
            <div className="rounded-lg border border-amber-200 bg-amber-50 dark:border-amber-800 dark:bg-amber-950/30 p-3 space-y-2 text-sm">
              <p className="font-medium text-amber-800 dark:text-amber-300">⚠️ {t("moveBooking.priceChange")}</p>
              <div className="flex items-center gap-2">
                <span>{formatPrice(resolveMoney(booking.price_total_minor, booking.price_total, booking.currency) ?? { valueMinor: 0, currency: "SEK" })}</span>
                <ArrowRight className="h-3 w-3" />
                <span className="font-semibold">{formatPrice({ valueMinor: targetPrice.totalMinor, currency: booking.currency })}</span>
                <Badge variant={deltaMinor > 0 ? "destructive" : "default"} className="text-xs">
                  {deltaMinor > 0 ? "+" : ""}{formatPrice({ valueMinor: deltaMinor, currency: booking.currency })}
                </Badge>
              </div>
              <Select value={priceAction} onValueChange={(v) => setPriceAction(v as "update" | "keep" | "manual")}>
                <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {deltaMinor > 0 ? (
                    <>
                      {/* UPPDRAG-33 (Q1=A): "Debitera mellanskillnad" requires a
                          saved MIT-enabled card. If the customer doesn't have
                          one, disable this option (admin must waive or set a
                          manual price instead). */}
                      <SelectItem value="update" disabled={!hasSavedCard}>
                        {hasSavedCard
                          ? t("moveBooking.chargeDiff", { amount: formatPrice({ valueMinor: deltaMinor, currency: booking.currency }) })
                          : t("moveBooking.chargeDiffNoCardItem", {
                              defaultValue: "Debitera mellanskillnad — kunden har inget sparat kort",
                            })}
                      </SelectItem>
                      <SelectItem value="keep">{t("moveBooking.waiveDiff")}</SelectItem>
                    </>
                  ) : (
                    <>
                      <SelectItem value="update">{t("moveBooking.refundDiff", { amount: formatPrice({ valueMinor: Math.abs(deltaMinor), currency: booking.currency }) })}</SelectItem>
                      <SelectItem value="keep">{t("moveBooking.keepPrice")}</SelectItem>
                    </>
                  )}
                  <SelectItem value="manual">{t("moveBooking.manualPrice")}</SelectItem>
                </SelectContent>
              </Select>
              {priceAction === "manual" && (
                <Input type="number"
                  value={manualPrice}
                  onChange={(e) => setManualPrice(e.target.value)}
                  placeholder={t("moveBooking.enterPricePlaceholder")}
                  className="h-8 text-sm"
                />
              )}
              {/* UPPDRAG-33: inline hint when "Debitera" is unavailable
                  due to no saved card. Always renders for deltaMinor > 0
                  + no card so admin sees why the option is disabled. */}
              {deltaMinor > 0 && !hasSavedCard && (
                <p className="text-xs text-amber-700 dark:text-amber-400">
                  {t("moveBooking.chargeDiffNoCardHint", {
                    defaultValue:
                      "Kunden har inget sparat MIT-kort. Välj \"Bjud på mellanskillnaden\" eller \"Manuellt pris\" istället.",
                  })}
                </p>
              )}
            </div>
          )}

          {/* Reason */}
          <div>
            <Label className="text-xs">{t("moveBooking.reasonLabel")}</Label>
            <Textarea
              value={reason}
              onChange={e => setReason(e.target.value)}
              placeholder={t("moveBooking.reasonPlaceholder")}
              rows={2}
            />
          </div>

          {/* Notify checkboxes */}
          <div className="space-y-2">
            <label className="flex items-center gap-2 text-sm">
              <Checkbox checked={notifyBooker} onCheckedChange={(v) => setNotifyBooker(!!v)} />
              {t("moveBooking.notifyBooker")}
            </label>
            <label className="flex items-center gap-2 text-sm">
              <Checkbox checked={notifyPlayers} onCheckedChange={(v) => setNotifyPlayers(!!v)} />
              {t("moveBooking.notifyPlayers")}
            </label>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>{t("moveBooking.cancel")}</Button>
          <Button
            onClick={handleMove}
            // UPPDRAG-33: also block submit when admin has selected
            // "Debitera mellanskillnad" but the customer has no saved card.
            // The select item is already disabled, but priceAction='update'
            // is the default initial state, so we double-guard here.
            disabled={
              !hasChanged ||
              hasOverlap ||
              !withinHours ||
              moving ||
              (deltaMinor > 0 && priceAction === "update" && !hasSavedCard)
            }
          >
            {moving ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
            {t("moveBooking.title")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
